import angular from 'angular';

/**
 * Centralized routes configuration for the application.
 * All UI-Router states are defined here.
 */
export default function configureRoutes($stateProvider, $urlRouterProvider) {
  'ngInject';

  // index.html is the built entry file, but the app route is /
  $urlRouterProvider.when('/index.html', '/');
  $urlRouterProvider.when('/403', '/unauthorized');
  $urlRouterProvider.when('/404', '/notfound');

  // Default route
  $urlRouterProvider.otherwise('/notfound');

  // Helper function for permission checking
  const checkPermission = function ($http, $rootScope, $state, permissionName) {
    $http.get($rootScope.apiHost+"/api/users/me", {withCredentials: true}).then(function (response) {
      let hasPermission = false;

      response.data.roles.forEach(function (role) {
        role.permissions.forEach(function (permission) {
          if (permission.name === permissionName) {
            hasPermission = true;
          }
        });
      });

      if(!hasPermission){
        console.log('going unauthorized');
        $state.go("unauthorized");
      }
    });
  };

  // Define all application states
  $stateProvider
    // App state - parent state for all routes
    .state('app', {
      abstract: true,
      template: '<div class="whole-screen-container"><header></header><page-header></page-header><ui-view></ui-view></div>',
      resolve: {
        currentUser: function (AuthenticationService) {
          return AuthenticationService.loadUser();
        }
      },
      onEnter: function ($rootScope, $state, currentUser) {
        if (!currentUser || currentUser.roles.length === 0) {
          $state.go("login");
        }
      }
    })

    .state('home', {
      parent: 'app',
      url: '/',
      template: '<admin></admin>',
      onEnter: function ($rootScope, $state, currentUser) {
        let defHome = 'unauthorized';
        let adminDashboard = false;
        let timesheet = false;
        currentUser.roles.forEach(function (role) {
          role.permissions.forEach(function (permission) {
            if (permission.name === 'PERM_ADMIN_DASHBOARD') {
              adminDashboard = true;
            }
            if (permission.name === 'PERM_USER_TIMESHEET') {
              timesheet = true;
            }
          });
        });

        if (adminDashboard) {
          defHome = 'admin.dashboard';
        } else if (timesheet) {
          defHome = 'timesheet';
        }

        $state.go(defHome);
      }
    })

    // Admin states
    .state('admin', {
      parent: 'app',
      url: '/admin',
      template: '<admin></admin>',
    })

    // Timesheet state
    .state('timesheet', {
      parent: 'app',
      url: '/timesheet',
      template: '<timesheet></timesheet>',
      data: {
        pageHeader: false,
        pageTitle: 'Timesheet',
        pageDesc: ''
      }
    })

    // Leave state
    .state('leave', {
      parent: 'app',
      url: '/leave',
      template: '<leave></leave>',
      data: {
        pageHeader: true,
        pageTitle: 'Leave',
        pageDesc: 'View your leave balance and usage'
      }
    })

    // 404 Not Found state
    .state('notfound', {
      url: '/notfound',
      template: '<notfound></notfound>'
    })

    // 403 Unauthorized state
    .state('unauthorized', {
      url: '/unauthorized',
      template: '<unauthorized></unauthorized>'
    })

    // Login state
    .state('login', {
      url: '/login',
      template: '<login></login>',
      onEnter: function($http, $rootScope, $state, $location, AuthenticationService) {
        AuthenticationService.isAuthenticated().then(function (response) {
          $state.go('admin');
        });
      },
      params: {
        loginErrorCode: ''
      }
    })

    // Admin Dashboard
    .state('admin.dashboard', {
      url: '/dashboard',
      parent: 'admin',
      template: '<dashboard></dashboard>',
      data: {
        pageHeader: false
      }
    })

    // Admin Employees
    .state('admin.employees', {
      url: '/employees',
      parent: 'admin',
      template: '<employees></employees>',
      data: {
        pageHeader: true,
        pageTitle: 'Employees',
        pageDesc: 'Employees Listing'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_EMPLOYEES");
      }
    })

    // Admin Employee Create
    .state('admin.employee-create', {
      url: '/employees/create',
      parent: 'admin',
      template: '<employee-create></employee-create>',
      data: {
        pageHeader: true,
        pageTitle: 'Create an Employee',
        pageDesc: 'Create Employee'
      }
    })

    // Admin Employee Details
    .state('admin.employee-details', {
      url: '/employees/:employeeId',
      parent: 'admin',
      template: '<employee-details></employee-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Employee Details',
        pageDesc: 'Employees Listing',
        breadcrumbLink: 'admin.employees',
        breadcrumbText: 'View All Employees'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_MANAGE_EMPLOYEE_DETAILS");
      }
    })

    // Admin Customers
    .state('admin.customers', {
      url: '/customers',
      parent: 'admin',
      template: '<customers></customers>',
      data: {
        pageHeader: true,
        pageTitle: 'Customers',
        pageDesc: 'Manage Customers'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_CUSTOMERS");
      }
    })

    // Admin Data Management
    .state('admin.data-management', {
      url: '/data-management',
      parent: 'admin',
      template: '<information-management></information-management>',
      data: {
        pageHeader: true,
        pageTitle: 'Data Management',
        pageDesc: 'Manage Data',
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_MANAGE_PERMISSIONS");
      }
    })

    // Admin Customer Details
    .state('admin.customer-details', {
      url: '/customers/:customerId',
      parent: 'admin',
      template: '<customer-details></customer-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Customer Details',
        pageDesc: 'Manage Customer',
        breadcrumbLink: 'admin.customers',
        breadcrumbText: 'View All Customers'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_CUSTOMERS");
      }
    })

    // Admin Contracts
    .state('admin.contracts', {
      url: '/contracts',
      parent: 'admin',
      template: '<contracts></contracts>',
      data: {
        pageHeader: true,
        pageTitle: 'Contracts',
        pageDesc: 'Manage Contracts'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_CONTRACTS");
      }
    })

    // Admin Projects
    .state('admin.projects', {
      url: '/projects',
      parent: 'admin',
      template: '<projects></projects>',
      data: {
        pageHeader: true,
        pageTitle: 'Projects',
        pageDesc: 'Manage Projects'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_PROJECTS");
      }
    })

    // Admin Contract Details
    .state('admin.contract-details', {
      url: '/contracts/:contractId',
      parent: 'admin',
      template: '<contract-details></contract-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Contract Details',
        pageDesc: 'Manage Contract',
        breadcrumbLink: 'admin.contracts',
        breadcrumbText: 'View All Contracts'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_CONTRACTS");
      }
    })

    // Admin Project Details
    .state('admin.project-details', {
      url: '/projects/:projectId',
      parent: 'admin',
      template: '<project-details></project-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Project Details',
        pageDesc: 'Manage Project',
        breadcrumbLink: 'admin.projects',
        breadcrumbText: 'View All Projects'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_PROJECTS");
      }
    })

    // Legacy cost-plus URL — redirect to the single project details shell
    .state('admin.project-details-cost-plus', {
      url: '/projects/:projectId/cost-plus',
      parent: 'admin',
      onEnter: function($state, $stateParams) {
        $state.go('admin.project-details', { projectId: $stateParams.projectId }, { location: 'replace' });
      }
    })

    // Admin Charge Codes
    .state('admin.chargecodes', {
      url: '/chargecodes',
      parent: 'admin',
      template: '<chargecodes></chargecodes>',
      data: {
        pageHeader: true,
        pageTitle: 'Charge Codes',
        pageDesc: 'Manage Charge Codes'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_CHARGE_CODES");
      }
    })

    // Admin Charge Code Create
    .state('admin.chargecode-create', {
      url: '/chargecodes/create',
      parent: 'admin',
      template: '<chargecode-create></chargecode-create>',
      data: {
        pageHeader: true,
        pageTitle: 'Create a Charge Code',
        pageDesc: 'Create Charge Code'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_CREATE_CHARGE_CODES");
      }
    })

    // Admin Charge Code Details
    .state('admin.chargecode-details', {
      url: '/chargecodes/:chargecodeId',
      parent: 'admin',
      template: '<chargecode-details></chargecode-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Charge Code Details',
        pageDesc: 'Manage Charge Codes',
        breadcrumbLink: 'admin.chargecodes',
        breadcrumbText: 'View All Charge Codes'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_CHARGE_CODES");
      }
    })

    // Admin Invoices
    .state('admin.invoices', {
      url: '/invoices',
      parent: 'admin',
      template: '<invoices></invoices>',
      data: {
        pageHeader: true,
        pageTitle: 'Invoices',
        pageDesc: 'Manage Invoices'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_INVOICES");
      }
    })

    // Admin Invoice Details
    .state('admin.invoice-details', {
      url: '/invoices/:invoiceId',
      parent: 'admin',
      template: '<invoice-details></invoice-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Invoice Details',
        pageDesc: 'Manage Invoices',
        breadcrumbLink: 'admin.invoices',
        breadcrumbText: 'View All Invoices'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_INVOICES");
      }
    })

    // Admin Invoice Create Wizard
    .state('admin.invoice-create-wizard', {
      url: '/invoice/create',
      parent: 'admin',
      template: '<invoice-create-wizard></invoice-create-wizard>',
      data: {
        pageHeader: true,
        pageTitle: 'Create Invoice',
        pageDesc: 'Select the type of invoice to create'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_CREATE_INVOICE");
      }
    })

    // Admin Labor Invoice Create
    .state('admin.invoice-create', {
      url: '/invoice/create/labor',
      parent: 'admin',
      template: '<invoice-create></invoice-create>',
      data: {
        pageHeader: true,
        pageTitle: 'Create Labor Invoice',
        pageDesc: 'Generate a labor invoice from approved time entries'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_CREATE_INVOICE");
      }
    })

    // Admin Travel Invoice Create
    .state('admin.invoice-create-travel', {
      url: '/invoice/create/travel',
      parent: 'admin',
      template: '<invoice-create-travel></invoice-create-travel>',
      data: {
        pageHeader: true,
        pageTitle: 'Create Travel Invoice',
        pageDesc: 'Generate a travel invoice from approved expense reports'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_CREATE_INVOICE");
      }
    })

    // Admin Other Invoice Create
    .state('admin.invoice-create-other', {
      url: '/invoice/create/other',
      parent: 'admin',
      template: '<invoice-create-other></invoice-create-other>',
      data: {
        pageHeader: true,
        pageTitle: 'Create Other Invoice',
        pageDesc: 'Generate an invoice for miscellaneous billable items'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_CREATE_INVOICE");
      }
    })

    // Admin Settings
    .state('admin.settings', {
      url: '/settings',
      parent: 'admin',
      template: '<settings></settings>',
      data: {
        pageHeader: true,
        pageTitle: 'Company Settings',
        pageDesc: 'Manage the Settings for your Company'
      },
      onEnter: function($http, $rootScope, $state) {
        $http.get($rootScope.apiHost + '/api/users/me', { withCredentials: true }).then(function (response) {
          let hasPermission = false;

          response.data.roles.forEach(function (role) {
            role.permissions.forEach(function (permission) {
              if (permission.name === 'PERM_VIEW_COMPANY_SETTINGS' || permission.name === 'PERM_MANAGE_COMPANY_SETTINGS') {
                hasPermission = true;
              }
            });
          });

          if (!hasPermission) {
            $state.go('unauthorized');
          }
        });
      }
    })

    // Admin Company Benefits
    .state('admin.company-benefits', {
      url: '/settings/company-benefits',
      parent: 'admin',
      template: '<company-benefits></company-benefits>',
      data: {
        pageHeader: true,
        pageTitle: 'Company Benefits',
        pageDesc: 'Manage the Settings for your Company'
      }
    })

    // Admin Reports
    .state('admin.reports', {
      url: '/reports',
      parent: 'admin',
      template: '<reports></reports>',
      data: {
        pageHeader: true,
        pageTitle: 'Reports',
        pageDesc: 'View Reports'
      }
    })

    // Admin Integrations
    .state('admin.integrations', {
      url: '/integrations',
      parent: 'admin',
      template: '<integrations></integrations>',
      data: {
        pageHeader: true,
        pageTitle: 'Integrations',
        pageDesc: 'Integrations'
      }
    })

    // Admin Expenses
    .state('admin.expenses', {
      url: '/expense-reports/review',
      parent: 'admin',
      template: '<expense-review></expense-review>',
      data: {
        pageHeader: true,
        pageTitle: 'Review Expense Reports',
        pageDesc: 'Review Expense Reports'
      }
    })

    // Admin Permissions
    .state('admin.permissions', {
      url: '/permissions',
      parent: 'admin',
      template: '<role-management></role-management>',
      data: {
        pageHeader: true,
        pageTitle: 'Manage Permissions',
        pageDesc: 'Manage Permissions'
      },
      onEnter: function ($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_MANAGE_PERMISSIONS");
      }
    })

    // Admin Payroll
    .state('admin.payroll', {
      url: '/payroll',
      parent: 'admin',
      template: '<payroll></payroll>',
      data: {
        pageHeader: true,
        pageTitle: 'Payroll',
        pageDesc: 'View Payroll Data',
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_ACCOUNTING_INTEGRATION");
      }
    })

    // Admin Subcontractors
    .state('admin.subcontractors', {
      url: '/subcontractors',
      parent: 'admin',
      template: '<subcontractors></subcontractors>',
      data: {
        pageHeader: true,
        pageTitle: 'Subcontractors',
        pageDesc: 'Manage Subcontractors'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_CUSTOMERS");
      }
    })

    // Admin Subcontractor Details
    .state('admin.subcontractor-details', {
      url: '/subcontractors/:subcontractorId',
      parent: 'admin',
      template: '<subcontractor-details></subcontractor-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Subcontractor Details',
        pageDesc: 'Manage Subcontractors',
        breadcrumbLink: 'admin.subcontractors',
        breadcrumbText: 'View All Subcontractors'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_CUSTOMERS");
      }
    })

    // Admin Subcontracts
    .state('admin.subcontracts', {
      url: '/subcontracts',
      parent: 'admin',
      template: '<subcontracts></subcontracts>',
      data: {
        pageHeader: true,
        pageTitle: 'Subcontracts',
        pageDesc: 'Manage Subcontracts',
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_CUSTOMERS");
      }
    })

    // Admin Subcontract Details
    .state('admin.subcontract-details', {
      url: '/subcontracts/:subcontractId',
      parent: 'admin',
      template: '<subcontract-details></subcontract-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Subcontract Details',
        pageDesc: 'Manage Subcontracts',
        breadcrumbLink: 'admin.subcontracts',
        breadcrumbText: 'View All Subcontracts'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_VIEW_CUSTOMERS");
      }
    })

    // Admin Payroll Details
    .state('admin.payroll-details', {
      url: '/payroll/:payrollId',
      parent: 'admin',
      template: '<payroll-details></payroll-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Payroll Details',
        pageDesc: 'View Payroll Details',
        breadcrumbLink: 'admin.payroll',
        breadcrumbText: 'View All Payroll'
      },
      onEnter: function($http, $rootScope, $state) {
        checkPermission($http, $rootScope, $state, "PERM_ACCOUNTING_INTEGRATION");
      }
    })

    // Expenses states

    // Expenses main
    .state('expenses', {
      parent: 'app',
      url: '/expenses',
      template: '<expenses></expenses>',
      data: {
        pageHeader: true,
        pageTitle: 'Expenses',
        pageDesc: 'My Expenses'
      }
    })

    // Expense Wizard
    .state('expense-wizard', {
      parent: 'app',
      url: '/expense-report/create',
      template: '<expense-wizard></expense-wizard>',
      data: {
        pageHeader: true,
        pageTitle: 'Create an Expense Report',
        pageDesc: 'Create Expense Report'
      }
    })

    // Travel Expense (create or continue draft)
    .state('travel-expense', {
      parent: 'app',
      url: '/expenses/create/travel/:expenseReportId?proxyImport',
      template: '<travel-expense></travel-expense>',
      data: {
        pageHeader: true,
        pageTitle: 'Travel Expense Report',
        pageDesc: 'Travel Expense Report'
      }
    })

    // Travel Expense Details
    .state('travel-expense-details', {
      parent: 'app',
      url: '/expenses/travel/:expenseReportId',
      template: '<travel-expense-details></travel-expense-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Travel Expense Report',
        pageDesc: 'Travel Expense Report'
      }
    })

    // Travel Expense Actuals
    .state('travel-expense-actuals', {
      parent: 'app',
      url: '/expenses/travel/:expenseReportId/actuals?proxyImport',
      template: '<travel-expense-actuals></travel-expense-actuals>',
      data: {
        pageHeader: true,
        pageTitle: 'Travel Expense Actuals',
        pageDesc: 'Enter actual expenses and submit for reimbursement',
        breadcrumbLink: 'expenses',
        breadcrumbText: 'View All Expenses'
      }
    })

    // General Business (create or continue draft)
    .state('general-business', {
      parent: 'app',
      url: '/expenses/create/general-business/:expenseReportId?proxyImport',
      template: '<general-business></general-business>',
      data: {
        pageHeader: true,
        pageTitle: 'General Business Expense Report',
        pageDesc: 'General Business Expense Report'
      }
    })

    // General Business Details
    .state('general-business-details', {
      parent: 'app',
      url: '/expenses/general-business/:expenseReportId',
      template: '<general-business-details></general-business-details>',
      data: {
        pageHeader: true,
        pageTitle: 'General Business Expense Report',
        pageDesc: 'General Business Expense Report'
      }
    })

    // General Business Actuals
    .state('general-business-actuals', {
      parent: 'app',
      url: '/expenses/general-business/:expenseReportId/actuals?proxyImport',
      template: '<general-business-actuals></general-business-actuals>',
      data: {
        pageHeader: true,
        pageTitle: 'General Business Actuals',
        pageDesc: 'Enter actual costs and submit for reimbursement',
        breadcrumbLink: 'expenses',
        breadcrumbText: 'View All Expenses'
      }
    })

    // General Business Review Details (admin)
    .state('general-business-review-details', {
      parent: 'app',
      url: '/expenses/review/general-business/:expenseReportId',
      template: '<general-business-review-details></general-business-review-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Review Purchase Request',
        pageDesc: 'Review Purchase Request',
        breadcrumbLink: 'admin.expenses',
        breadcrumbText: 'View All Expense Reports'
      }
    })

    // Other Direct Costs (create or continue draft)
    .state('other-direct-costs', {
      parent: 'app',
      url: '/expenses/create/other-direct-costs/:expenseReportId?proxyImport',
      template: '<other-direct-costs></other-direct-costs>',
      data: {
        pageHeader: true,
        pageTitle: 'Other Direct Costs Expense Report',
        pageDesc: 'Other Direct Costs Expense Report'
      }
    })

    // Other Direct Costs Details
    .state('other-direct-costs-details', {
      parent: 'app',
      url: '/expenses/other-direct-costs/:expenseReportId',
      template: '<other-direct-costs-details></other-direct-costs-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Other Direct Costs Expense Report',
        pageDesc: 'Other Direct Costs Expense Report'
      }
    })

    // Other Direct Costs Actuals
    .state('other-direct-costs-actuals', {
      parent: 'app',
      url: '/expenses/other-direct-costs/:expenseReportId/actuals?proxyImport',
      template: '<other-direct-costs-actuals></other-direct-costs-actuals>',
      data: {
        pageHeader: true,
        pageTitle: 'Other Direct Costs Actuals',
        pageDesc: 'Enter actual costs and submit for reimbursement',
        breadcrumbLink: 'expenses',
        breadcrumbText: 'View All Expenses'
      }
    })

    // Other Direct Costs Review Details (admin)
    .state('other-direct-costs-review-details', {
      parent: 'app',
      url: '/expenses/review/other-direct-costs/:expenseReportId',
      template: '<other-direct-costs-review-details></other-direct-costs-review-details>',
      data: {
        pageHeader: true,
        pageTitle: 'Review Other Direct Costs',
        pageDesc: 'Review Other Direct Costs',
        breadcrumbLink: 'admin.expenses',
        breadcrumbText: 'View All Expense Reports'
      }
    })

    // Profile state
    .state('profile', {
      parent: 'app',
      url: '/profile',
      template: '<profile></profile>',
      data: {
        pageHeader: true,
        pageTitle: 'My Profile',
        pageDesc: 'Manage information about your account'
      }
    })

    // Review state
    .state('review', {
      parent: 'app',
      url: '/review',
      template: '<review></review>',
      data: {
        pageHeader: true,
        pageTitle: 'Timesheet Review',
        pageDesc: 'Review/Approve User Timesheets'
      }
    });
}



// WEBPACK FOOTER //
// ./client/routes.js